import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule, Signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { KindeAngularModule } from 'kinde-angular';

import { AuthInterceptorService, HasPermissionDirective } from '@eiq/auth';
import { CacheInterceptor, SystemService } from '@eiq/shared/data';
import { HttpErrorInterceptor } from '@eiq/shared/utils';

import { UiAdminModule } from '@eiq/ui';

import { MonitoringService } from '@empathy/global/utils';
import { SURVEY_DEFAULT_LANGUAGE_CODE } from '@empathy/survey-core';

import { AppComponent } from './app.component';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { KindeConfigurations } from '../environments/kinde';

const createTranslateLoader = (http: HttpClient): TranslateHttpLoader => new TranslateHttpLoader(http, '../assets/i18n/', '.json');

const initMonitoringService = (monitoringService: MonitoringService) => async (): Promise<void> => {
  if (!environment.appInsights.instrumentationKey) {
    return;
  }

  await monitoringService.init();
  monitoringService.addTelemetryInitializer((item) => {
    item.tags = item.tags ?? {};
    item.data = item.data ?? {};
    item.tags['eiqTenantName'] = location.host;
    item.data['eiqTenantName'] = location.host;
  });
};

const kindeConfSettings = KindeConfigurations[location.hostname];

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FontAwesomeModule,
    HttpClientModule,
    HasPermissionDirective,
    KindeAngularModule.forRoot(kindeConfSettings),
    MatIconModule,
    ReactiveFormsModule,
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules }),
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    UiAdminModule
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: APP_INITIALIZER, useFactory: initMonitoringService, deps: [MonitoringService], multi: true },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: 'UTC' } },
    { provide: 'environment', useValue: environment },
    {
      provide: SURVEY_DEFAULT_LANGUAGE_CODE,
      useFactory: (systemService: SystemService): Signal<string> => systemService.defaultLanguage$,
      deps: [SystemService]
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
